import { ArchiveIcon, ArchiveRestoreIcon, RefreshCwIcon, ShredderIcon } from 'lucide-react';
import { useState } from 'react';

import { FulfillmentStrategy, SkuAction, SkuType, type SkuDTO } from '@invenco/products-interface';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
} from '@/components/alert-dialog-with-action';
import { Button } from '@/components/core/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import { HeaderActions } from '@/components/header';
import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { skuTypeIcon, skuTypeTitle } from '@/shared/title-maps';

type ConvertAction =
  | SkuAction.CONVERT_TO_BUNDLE
  | SkuAction.CONVERT_TO_MYSTERY
  | SkuAction.CONVERT_TO_ALIAS
  | SkuAction.CONVERT_TO_STANDARD;

const conversionActionToSkuType: Record<ConvertAction, SkuType> = {
  [SkuAction.CONVERT_TO_BUNDLE]: SkuType.BUNDLE,
  [SkuAction.CONVERT_TO_MYSTERY]: SkuType.MYSTERY,
  [SkuAction.CONVERT_TO_ALIAS]: SkuType.ALIAS,
  [SkuAction.CONVERT_TO_STANDARD]: SkuType.STANDARD,
};

const skuTypeToFulfillmentStrategy: { [key in SkuType]: FulfillmentStrategy } = {
  [SkuType.BUNDLE]: FulfillmentStrategy.BUNDLE,
  [SkuType.MYSTERY]: FulfillmentStrategy.BUNDLE,
  [SkuType.ALIAS]: FulfillmentStrategy.BUNDLE,
  [SkuType.STANDARD]: FulfillmentStrategy.STOCK,
};

export function SkuActions({
  sku,
  skuId,
  onRefresh,
}: {
  sku?: SkuDTO;
  skuId?: string;
  onRefresh?: () => Promise<void>;
}) {
  const { productsGateway } = useGateways();
  const [confirmingConversionType, setConfirmingConversionType] = useState<SkuType>();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { mutate: archiveSku, isPending: isArchiving } = useGatewayMutation({
    mutationFn: () => productsGateway.archiveSku(skuId!),
    linkedQuery: ['skus', skuId],
    successMessage: 'SKU archived',
  });

  const { mutate: unarchiveSku, isPending: isUnarchiving } = useGatewayMutation({
    mutationFn: () => productsGateway.unarchiveSku(skuId!),
    linkedQuery: ['skus', skuId],
    successMessage: 'SKU restored',
  });

  const { mutate: convertSku, isPending: isConverting } = useGatewayMutation({
    mutationFn: (type: SkuType) => {
      const fulfillmentStrategy = skuTypeToFulfillmentStrategy[type];
      return productsGateway.convertSku(skuId!, { fulfillmentStrategy, type });
    },
    linkedQuery: ['skus', skuId],
    successMessage: 'SKU converted',
  });

  const convertDropdownMenuItems: SkuType[] = (sku?.allowableActions ?? [])
    .filter(
      (action): action is ConvertAction =>
        action === SkuAction.CONVERT_TO_BUNDLE ||
        action === SkuAction.CONVERT_TO_MYSTERY ||
        action === SkuAction.CONVERT_TO_ALIAS ||
        action === SkuAction.CONVERT_TO_STANDARD,
    )
    .map((action) => conversionActionToSkuType[action]);

  const startConversion = async (type: SkuType) => {
    if (sku?.components?.length || sku?.componentGroups?.length) {
      setConfirmingConversionType(type);
      return;
    }

    await convertSku(type);
  };

  const confirmConversion = async () => {
    if (!confirmingConversionType) {
      return Result.fail('No conversion type selected');
    }

    const result = await convertSku(confirmingConversionType);
    if (result.isSuccess) {
      setConfirmingConversionType(undefined);
    }
    return result;
  };

  const refresh = async () => {
    if (!onRefresh) {
      return;
    }

    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <HeaderActions>
        {sku?.allowableActions?.includes(SkuAction.ARCHIVE) ? (
          <Button variant="outline" loading={isArchiving} onClick={() => void archiveSku()}>
            <ArchiveIcon /> Archive
          </Button>
        ) : null}
        {sku?.allowableActions?.includes(SkuAction.UNARCHIVE) ? (
          <Button variant="outline" loading={isUnarchiving} onClick={() => void unarchiveSku()}>
            <ArchiveRestoreIcon /> Restore
          </Button>
        ) : null}
        {convertDropdownMenuItems.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" loading={isConverting}>
                <ShredderIcon /> Convert
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {convertDropdownMenuItems.map((type) => {
                const Icon = skuTypeIcon[type];
                return (
                  <DropdownMenuItem key={type} onSelect={() => void startConversion(type)}>
                    <Icon /> to {skuTypeTitle[type]}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        <Button
          size="icon"
          variant="outline"
          aria-label="Refresh"
          loading={isRefreshing}
          onClick={() => void refresh()}
        >
          <RefreshCwIcon />
        </Button>
      </HeaderActions>

      <AlertDialogWithAction
        open={!!confirmingConversionType}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmingConversionType(undefined);
          }
        }}
        onAction={confirmConversion}
      >
        <AlertDialogWithActionContent>
          <AlertDialogWithActionHeader>
            <AlertDialogWithActionTitle>
              Are you sure you want to convert this SKU?
            </AlertDialogWithActionTitle>
            <AlertDialogWithActionDescription>
              Converting the SKU&apos;s type from{' '}
              <strong>{sku?.type && skuTypeTitle[sku.type].toLocaleLowerCase()}</strong> to{' '}
              <strong>
                {confirmingConversionType &&
                  skuTypeTitle[confirmingConversionType].toLocaleLowerCase()}
              </strong>{' '}
              will erase all components.
            </AlertDialogWithActionDescription>
          </AlertDialogWithActionHeader>
          <AlertDialogWithActionFooter>
            <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
            <AlertDialogWithActionSubmit>Convert</AlertDialogWithActionSubmit>
          </AlertDialogWithActionFooter>
        </AlertDialogWithActionContent>
      </AlertDialogWithAction>
    </>
  );
}
